import React, { useContext, useState } from "react";
import { Box, Button, Grid, Paper, TextField, Autocomplete } from "@mui/material";
import { CepService } from "../../Services/CepService";
import { ClienteService } from "../../Services/ClienteService";
import { AppProvider } from "../../ContextApi/AppProvider";
import {Cliente, FormPesquisarCliente} from "../Shared/Interfaces/Cliente";
import SearchIcon from "@mui/icons-material/Search";
import DefaultLoader from "../Shared/DefaultLoader";
import {
  limparMascaraDocumento,
  cpfApplyMask,
  cnpjApplyMask,
} from "../../Utils/utils";
import { CadastroService } from "../../Services/CadastrosService";
import { TipoCadastro } from "../Shared/Enums/TipoCadastro";
import DataItem from "../Shared/Interfaces/DataItem";
import colors from "../../theme/colors";

const cepService = new CepService();
const clienteService = new ClienteService();
const cadastroService = new CadastroService();

export default function CadastrarClientes() {
  const contextApi = useContext(AppProvider);
  const [clienteDocumento, setClienteDocumento] = React.useState("");
  const [loading, setLoading] = useState(false);
  const [canaisDeComunicacao, setCanaisDeComunicacao] = React.useState<DataItem[]>([]);

  const initialStateCliente: Cliente = {
    id: 0,
    nome: "",
    documento: "",
    telefone: "",
    email: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    comoConheceu: "",
  };

  const [cliente, setCliente] = React.useState<Cliente>(initialStateCliente);

  const handleInputCliente = (fieldName: keyof Cliente, value: string) => {
    setCliente((prevCliente) => ({
      ...prevCliente,
      [fieldName]: value,
    }));
  };

  const handlePastecep = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent the default paste action
    const pastedData = e.clipboardData.getData('Text');
    // Clean the pasted data and set the input value
    const cleanedData = limparMascaraDocumento(pastedData);
    handleInputCliente("cep", cleanedData);
  };


    const handleClienteDocumentoChange = (value: string) => {
      const onlyNumbers = limparMascaraDocumento(value);
  
      if (onlyNumbers.length === 11) {
        const cpf = cpfApplyMask(onlyNumbers);
        return setClienteDocumento(cpf);
      }
  
      if (onlyNumbers.length === 14) {
        const cnpj = cnpjApplyMask(onlyNumbers);
        return setClienteDocumento(cnpj);
      }
  
      setClienteDocumento(onlyNumbers);
    };

    const getCanaisDeComunicacao = async () => {
      try {
        const response = await cadastroService.getAll(
          `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.CanaisDeComunicacao}`
        );
        setCanaisDeComunicacao(response.data || []);
      } catch (e) {
        console.error("Erro ao buscar Canais de Comunicação:", e);
      }
    };

  const BuscarCientePorDocumento = (e: React.MouseEvent) => {
    const filtroPesquisa: FormPesquisarCliente = {
      nome: cliente.nome,
      documento: limparMascaraDocumento(clienteDocumento),
    };
    e.preventDefault();
    setCliente(initialStateCliente);
    if (clienteDocumento === "" && cliente.nome === "") {
      contextApi.showMessage("Informe um documento ou nome válido!", true, "error");
    } else {
      clienteService
        .buscarPor(filtroPesquisa)
        .then((response) => {
          setCliente(response.data[0]);
          setClienteDocumento(
            response.data[0].documento.length === 11
              ? cpfApplyMask(response.data[0].documento)
              : cnpjApplyMask(response.data[0].documento)
          );
        })
        .catch((error) => {
          handleInputCliente("documento", limparMascaraDocumento(clienteDocumento));
          contextApi.showMessage(error.response.data, true, "error");
        });
    }
  };

  const BuscarCep = (e: React.MouseEvent) => {
    e.preventDefault();
    if (cliente.cep === "") {
      contextApi.showMessage("Informe um cep válido!", true, "error");
    } else {
      cepService
        .getCep(`https://viacep.com.br/ws/${cliente.cep}/json/`)
        .then((response) => {
          handleInputCliente("bairro", response.data.bairro);
          handleInputCliente("complemento", response.data.complemento);
          handleInputCliente("cidade", response.data.localidade);
          handleInputCliente("logradouro", response.data.logradouro);
          handleInputCliente("estado", response.data.uf);
        });
    }
  };

  const EnviarCliente = (e: React.MouseEvent) => {
    setLoading(true);
    cliente.documento = limparMascaraDocumento(clienteDocumento);
    e.preventDefault();
    if(cliente.numero == ""){
      cliente.numero = null;
    }
    if (cliente.id !== 0) {
      clienteService
        .putCliente(cliente)
        .then((response) => {
          if (response.status === 200) {
            contextApi.showMessage(
              "Cliente atualizado com sucesso!",
              true,
              "success"
            );
            setCliente(initialStateCliente);
            setClienteDocumento("");
            setLoading(false);
          }
        })
        .catch((error) => {
          setCliente(initialStateCliente);
          setClienteDocumento("");
          contextApi.showMessage(error.response.data, true, "error");
          setLoading(false);
        });
    } else {
      clienteService
        .postCliente(cliente)
        .then((response) => {
          if (response.status === 201) {
            contextApi.showMessage(
              "Cliente cadastrado com sucesso!",
              true,
              "success"
            );
            setCliente(initialStateCliente);
            setClienteDocumento("");
            setLoading(false);
          }
        })
        .catch((error) => {
          setCliente(initialStateCliente);
          setClienteDocumento("");
          contextApi.showMessage(error.response.data, true, "error");
          setLoading(false);
        });
    }
  };

  React.useEffect(() => {
    getCanaisDeComunicacao();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 1 }}>
      <Paper sx={{ p: 1 }}>
        <Grid container spacing={1}>
          {/* Cabeçalho */}
          <Grid item xs={12}>
            <Paper sx={{ p: 0.5, bgcolor: '#f8f9fa', mb: 1 }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                👤 Cadastrar Cliente
              </h4>
            </Paper>
          </Grid>

          {/* Seção Dados do Cliente */}
          <Grid item xs={12}>
            <Paper sx={{ p: 0.5, mb: 0.5 }}>
              <h5 style={{ margin: '0 0 10px 0', color: colors.bluePrimary }}>📝 Dados do Cliente</h5>
              <Grid container spacing={1}>
            {/* Inicio Documento + Nome + Botão */}
            <Grid item xs={12} sm={2}>
              <TextField
                required
                name="documento"
                label="CPF/CNPJ"
                size="small"
                fullWidth
                variant="outlined"
                value={clienteDocumento}
                onChange={(e) => handleClienteDocumentoChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                required
                name="nome"
                label="Nome Cliente"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.nome}
                onChange={(e) => handleInputCliente("nome", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Button
                fullWidth
                sx={{ height: "40px" }}
                size="small"
                variant="contained"
                color="primary"
                onClick={BuscarCientePorDocumento}
              >
                <SearchIcon />
              </Button>
            </Grid>
            <Grid item xs={12} sm={5}></Grid>
            {/* Fim Documento + Nome + Botão */}
            <Grid item xs={12} sm={2}>
              <TextField
                required
                name="telefone"
                label="Telefone"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.telefone}
                onChange={(e) => handleInputCliente("telefone", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                required
                name="email"
                label="E-mail"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.email}
                onChange={(e) => handleInputCliente("email", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Autocomplete
                fullWidth
                options={canaisDeComunicacao}
                getOptionLabel={(option) => option.descricao}
                size="small"
                value={
                  canaisDeComunicacao.find(
                    (option) => option.descricao === cliente.comoConheceu
                  ) || null
                }
                onChange={(e, newValue) =>
                  handleInputCliente("comoConheceu", newValue?.descricao || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Por onde conheceu a loja?" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}></Grid>
            {/* CEP + Botão */}
            <Grid item xs={12} sm={2}>
              <TextField
                required
                name="cep"
                label="Cep"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.cep}
                onChange={(e) => handleInputCliente("cep", e.target.value)}
                onPaste={handlePastecep}
              />
            </Grid>

            <Grid item xs={12} sm={1}>
              <Button
                fullWidth
                sx={{ height: "40px" }}
                size="small"
                variant="contained"
                color="primary"
                onClick={BuscarCep}
              >
                <SearchIcon />
              </Button>
            </Grid>
            <Grid item xs={12} sm={9}></Grid>

            {/* Endereço */}
            <Grid item xs={12} sm={4}>
              <TextField
                required
                name="logradouro"
                label="Logradouro"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.logradouro}
                onChange={(e) => handleInputCliente("logradouro", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={1}>
              <TextField
                required
                name="numero"
                label="Nº"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.numero}
                onChange={(e) => handleInputCliente("numero", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                name="complemento"
                label="Complemento"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.complemento}
                onChange={(e) => handleInputCliente("complemento", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                required
                name="bairro"
                label="Bairro"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.bairro}
                onChange={(e) => handleInputCliente("bairro", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                required
                name="cidade"
                label="Cidade"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.cidade}
                onChange={(e) => handleInputCliente("cidade", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={1}>
              <TextField
                required
                name="estado"
                label="UF"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.estado}
                onChange={(e) => handleInputCliente("estado", e.target.value)}
              />
            </Grid>

              </Grid>
            </Paper>
          </Grid>

          {/* Botão de Salvar */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={EnviarCliente}
                disabled={loading}
                sx={{ height: '40px' }}
              >
                Salvar
              </Button>
              {loading && (
                <DefaultLoader
                  height="2.5em"
                  width="2.5em"
                  colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                  textInfo="Salvando cliente"
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
